/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@alfresco/adf-core';
import { race, timer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

/**
 * Starts the app afresh after signing out. The services keep what they read
 * about the signed-in person for the whole session — whether they are an
 * administrator, the menus open to them, the mail settings — and signing out
 * does not reload the page, so the next person to sign in on the same tab
 * would see the previous one's menus until a refresh. A reload once the
 * sign-in page is up clears all of it.
 */
export function reloadOnLogout(reload: () => void = () => window.location.reload()): void {
  const auth = inject(AuthenticationService);
  const router = inject(Router);

  auth.onLogout
    .pipe(
      // the ticket is gone by now; wait for the sign-in page (or a moment) so the reload lands there
      switchMap(() => race(router.events.pipe(filter((e) => e instanceof NavigationEnd)), timer(1500)).pipe(take(1))),
      take(1)
    )
    .subscribe(() => reload());
}
